/**
 * The microphone and the speaker of a live call, in the browser.
 *
 * The phone's `@/lib/live-audio` is this object over `react-native-audio-api`,
 * which cannot run here; this is the same object — the same members, the
 * same guarantees — over `microphone.ts` and `pcm-player.ts`, so the hooks read
 * the way the phone's do. `start` asks for the microphone and starts handing
 * frames over; `stop` gives both ends back; `play`, `flush`, `isSpeaking` and
 * `onSpeakingChange` are the speaker's.
 *
 * `era` does what it does on the phone: a stop and a start in the same breath
 * (a call ended and another begun) must not let the stop's awaited half close
 * the microphone the start has just opened.
 *
 * Live mode's ear (step 4.4): `start` takes the microphone's options (50 ms
 * frames, the track's and the capture context's events); `echoCancel` is the
 * track's own reading; `duck`, `unduck` and `playedMs` are the speaker's;
 * `onStateChange` hears the speaker's context; `resume` wakes both contexts
 * from the person's gesture after an interruption.
 */
import type { LiveSound } from '@alluzo/shared';

import { openMicrophone, type Microphone, type MicrophoneOptions } from './microphone';
import { createPcmPlayer } from './pcm-player';

export type LiveAudio = {
  /**
   * Ask for the microphone and start handing frames over. Rejects with a
   * `MicrophoneError` when the microphone is refused or will not start; the
   * caller says so and falls back to typing.
   */
  readonly start: (onFrame: (pcm: Uint8Array<ArrayBuffer>) => void, options?: MicrophoneOptions) => Promise<void>;
  /** Stop the microphone, drop what is scheduled, and let both go. Idempotent. */
  readonly stop: () => Promise<void>;
  /** One binary frame from the server: PCM16 little-endian mono at the output rate. */
  readonly play: (frame: Uint8Array) => void;
  /** The person cut in: drop every frame that has not been heard yet. */
  readonly flush: () => void;
  /** Whether there is still sound scheduled, read at this instant. */
  readonly isSpeaking: () => boolean;
  /** Told when that answer changes, so a screen can follow it without polling itself. */
  readonly onSpeakingChange: (listener: (speaking: boolean) => void) => void;
  /** How loud the voice plays, 0 to 1; the timing stays the same at any volume. */
  readonly setVolume: (volume: number) => void;
  /** One of live mode's earcons, beside the voice and never in its queue. */
  readonly earcon: (sound: LiveSound) => void;
  /** The microphone's track reads echo cancellation back on; false before `start`. */
  readonly echoCancel: () => boolean;
  /** How much of OS's current line has been heard, in ms, counted from its first frame. */
  readonly playedMs: () => number;
  /** The server began a new line of OS's (`speaking`): the next frame starts `playedMs` again. */
  readonly newLine: () => void;
  /** Turn the voice down under the person's (150 ms), and back. */
  readonly duck: () => void;
  readonly unduck: () => void;
  /** Told the speaker's context state when it changes. */
  readonly onStateChange: (listener: ((state: string) => void) | null) => void;
  /** Wake the speaker and the microphone after an interruption: call it from the person's gesture. */
  readonly resume: () => Promise<void>;
};

export const createLiveAudio = (): LiveAudio => {
  const player = createPcmPlayer();
  let microphone: Microphone | null = null;
  let era = 0;

  const start = async (onFrame: (pcm: Uint8Array<ArrayBuffer>) => void, options?: MicrophoneOptions): Promise<void> => {
    const mine = ++era;
    // The speaker first: it is the half a browser's autoplay rule is about,
    // and the click that asked for the call is nearest now.
    await player.open();
    if (era !== mine) return;
    const opened = await openMicrophone(onFrame, options);
    if (era !== mine) {
      await opened.stop();
      return;
    }
    microphone = opened;
  };

  const stop = async (): Promise<void> => {
    ++era;
    const held = microphone;
    microphone = null;
    await Promise.all([held?.stop() ?? Promise.resolve(), player.close()]);
  };

  return {
    start,
    stop,
    play: player.play,
    flush: player.flush,
    isSpeaking: player.isSpeaking,
    onSpeakingChange: (listener) => player.onSpeakingChange(listener),
    setVolume: player.setVolume,
    earcon: player.earcon,
    echoCancel: () => microphone?.echoCancel ?? false,
    playedMs: player.playedMs,
    newLine: player.newLine,
    duck: player.duck,
    unduck: player.unduck,
    onStateChange: (listener) => player.onStateChange(listener),
    resume: async () => {
      await player.open();
      await microphone?.resume();
    },
  };
};

